import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAccountStore } from '../../../entities/account';
import { useLoadAccounts } from '../../../features/accounts-load';
import type { Account } from '../../../entities/account';
import { accountInsightsAPI } from '../../../shared/api/account-insights.api';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from '../../../components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Loader2, RefreshCw, FileSpreadsheet, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { TableSkeleton } from '../../../shared/ui/TableSkeleton';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner@2.0.3';
import { PlatformBadge } from '../../../features/statistics/ui/PlatformBadge';
import { DateRangeSelector } from '../../../features/statistics/components/DateRangeSelector';
import { ExportMenu } from '../../../features/statistics/components/ExportMenu';
import { useDateRangeParams } from '../../../features/statistics/hooks/useDateRangeParams';
import { resolveDateRange } from '../../../features/statistics/lib/date-range';
import { mapWithConcurrency } from '../../../shared/lib/mapWithConcurrency';
import {
  accountsToLoad,
  statsKey,
  useAccountStatsCache,
  type AccountPeriodStats,
} from '../model/accountStatsCache';
import {
  getTikTokAccounts,
  getTikTokStats,
  getTikTokVideosInRange,
} from '../../../shared/api/tiktok.api';

interface TikTokRow {
  openId: string;
  displayName: string;
  username: string;
  followers: number;
  interactions: number;
  views: number;
  er: number | null;
  loading: boolean;
}

type AccountStats = AccountPeriodStats;

// Запросы insights по аккаунтам — не больше четырёх разом: 60 аккаунтов
// подряд шли минуту, все сразу упираются в лимиты Instagram.
const ACCOUNT_CONCURRENCY = 4;

type SortColumn = 'name' | 'followers' | 'totalInteractions' | 'views' | 'reach' | 'er';
type SortDirection = 'asc' | 'desc' | null;

export function AllAccountsStatsTable() {
  const { accounts, isLoading: isAccountsLoading } = useAccountStore();
  const { loadAccounts } = useLoadAccounts();
  // Период — общий для раздела «Статистика».
  const [dateRangeOption, setDateRangeOption] = useDateRangeParams();
  const resolvedRange = useMemo(() => resolveDateRange(dateRangeOption), [dateRangeOption]);
  const since = String(Math.floor(resolvedRange.from.getTime() / 1000));
  const until = String(Math.floor(resolvedRange.to.getTime() / 1000));
  const cache = useAccountStatsCache();
  // Цифры текущего периода по id аккаунта — в том виде, что ждёт таблица.
  const stats = useMemo(() => {
    const out: Record<string, AccountStats> = {};
    for (const a of accounts) {
      const v = cache.stats[statsKey(a.id, since, until)];
      if (v) out[a.id] = v;
    }
    return out;
  }, [cache.stats, accounts, since, until]);
  const loadingStats = useMemo(() => {
    const out: Record<string, boolean> = {};
    for (const a of accounts) out[a.id] = !!cache.loading[statsKey(a.id, since, until)];
    return out;
  }, [cache.loading, accounts, since, until]);
  const [loadingProgress, setLoadingProgress] = useState<string>('');
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const filteredAccounts = useMemo(() => {
    return accounts.filter(account => !account.tags?.includes('test'));
  }, [accounts]);

  // Sorting handler
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle: asc -> desc -> null -> asc
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortColumn(null);
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sorted accounts
  const sortedAccounts = useMemo(() => {
    if (!sortColumn || !sortDirection) {
      return filteredAccounts;
    }

    return [...filteredAccounts].sort((a, b) => {
      let aValue: number | string = 0;
      let bValue: number | string = 0;

      switch (sortColumn) {
        case 'name':
          aValue = a.name || a.username || '';
          bValue = b.name || b.username || '';
          break;
        case 'followers':
          aValue = a.followersCount || 0;
          bValue = b.followersCount || 0;
          break;
        case 'totalInteractions':
          aValue = stats[a.id]?.totalInteractions || 0;
          bValue = stats[b.id]?.totalInteractions || 0;
          break;
        case 'views':
          aValue = stats[a.id]?.views || 0;
          bValue = stats[b.id]?.views || 0;
          break;
        case 'reach':
          aValue = stats[a.id]?.reach || 0;
          bValue = stats[b.id]?.reach || 0;
          break;
        case 'er':
          aValue = stats[a.id]?.er || 0;
          bValue = stats[b.id]?.er || 0;
          break;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue, 'ru')
          : bValue.localeCompare(aValue, 'ru');
      }

      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [filteredAccounts, sortColumn, sortDirection, stats]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  // TikTok accounts shown in the same list. Followers come from the profile;
  // engagement/views are summed over videos in the selected period.
  const [tiktokRows, setTiktokRows] = useState<TikTokRow[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let accs: { openId: string; displayName: string; username: string }[] = [];
      try {
        accs = await getTikTokAccounts();
      } catch {
        return;
      }
      if (cancelled || accs.length === 0) return;
      setTiktokRows(accs.map((a) => ({
        openId: a.openId, displayName: a.displayName, username: a.username, followers: 0, interactions: 0, views: 0, er: null, loading: true,
      })));

      const from = resolvedRange.from.getTime();
      const to = resolvedRange.to.getTime();
      for (const a of accs) {
        try {
          const [stats, vids] = await Promise.all([
            getTikTokStats(a.openId),
            getTikTokVideosInRange(a.openId, from, to),
          ]);
          const views = vids.reduce((s, v) => s + v.viewCount, 0);
          const interactions = vids.reduce((s, v) => s + v.likeCount + v.commentCount + v.shareCount, 0);
          if (cancelled) return;
          setTiktokRows((prev) => prev.map((r) => r.openId === a.openId ? {
            ...r,
            followers: stats.followerCount,
            interactions,
            views,
            er: views > 0 ? (interactions / views) * 100 : null,
            loading: false,
          } : r));
        } catch {
          if (cancelled) return;
          setTiktokRows((prev) => prev.map((r) => r.openId === a.openId ? { ...r, loading: false } : r));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [resolvedRange]);

  const fetchStatsForAccount = async (account: Account): Promise<boolean> => {
    const key = statsKey(account.id, since, until);
    useAccountStatsCache.setState((st) => ({ loading: { ...st.loading, [key]: true } }));
    try {
      // Запрашиваем total_interactions, views и reach
      const accountInsights = await accountInsightsAPI.getAccountInsights(account.id, {
        metric: 'total_interactions,views,reach',
        period: 'day',
        metricType: 'total_value',
        since,
        until,
      });

      // Извлекаем значения метрик
      const interactionsMetric = accountInsights.data.find(m => m.name === 'total_interactions');
      const viewsMetric = accountInsights.data.find(m => m.name === 'views');
      const reachMetric = accountInsights.data.find(m => m.name === 'reach');

      const totalInteractions = interactionsMetric?.total_value?.value || 0;
      const totalViews = viewsMetric?.total_value?.value || 0;
      const totalReach = reachMetric?.total_value?.value || 0;

      // Рассчитываем ER по формуле: ER = (total_interactions / views) × 100
      const er = totalViews > 0 ? (totalInteractions / totalViews) * 100 : null;

      useAccountStatsCache.setState((st) => {
        const { [key]: _drop, ...failed } = st.failed;
        return {
          stats: { ...st.stats, [key]: { totalInteractions, views: totalViews, reach: totalReach, er } },
          failed,
        };
      });
      return true;
    } catch (error) {
      console.error(`Failed to fetch stats for account ${account.id}:`, error);
      useAccountStatsCache.setState((st) => ({ failed: { ...st.failed, [key]: true } }));
      return false;
    } finally {
      useAccountStatsCache.setState((st) => ({ loading: { ...st.loading, [key]: false } }));
    }
  };

  // Business/creator: у личных аккаунтов insights нет.
  const insightAccounts = useMemo(
    () => filteredAccounts.filter((a) => a.accountType !== 'PERSONAL'),
    [filteredAccounts],
  );

  const loadStats = useCallback(
    async (force: boolean) => {
      const ids = accountsToLoad(insightAccounts.map((a) => a.id), since, until, useAccountStatsCache.getState(), force);
      if (ids.length === 0) return;
      const todo = insightAccounts.filter((a) => ids.includes(a.id));
      setLoadingProgress(`Загрузка Instagram: 0/${todo.length}`);
      const settled = await mapWithConcurrency(todo, ACCOUNT_CONCURRENCY, fetchStatsForAccount, (done, total) =>
        setLoadingProgress(`Загрузка Instagram: ${done}/${total}`),
      );
      setLoadingProgress('');
      // Один итог вместо тоста на каждый аккаунт.
      const failed = settled.filter((r) => !r.ok || r.value === false).length;
      if (failed > 0) toast.error(`Не загрузилась статистика ${failed} из ${todo.length} аккаунтов — «Обновить», чтобы повторить`);
      else if (force) toast.success('Статистика обновлена');
    },
    // fetchStatsForAccount зависит только от since/until.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [insightAccounts, since, until],
  );

  // Сами догружаем при входе и смене периода; «Обновить» — принудительно.
  useEffect(() => {
    if (isAccountsLoading) return;
    void loadStats(false);
  }, [loadStats, isAccountsLoading]);

  const fetchAllStats = () => void loadStats(true);

  // Helper to format numbers
  const formatNumber = (num: number | undefined | null) => {
    if (num === undefined || num === null) return '—';
    return new Intl.NumberFormat('ru-RU').format(num);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Sortable header component
  const SortableHeader = ({ column, children, className = '' }: { 
    column: SortColumn; 
    children: React.ReactNode;
    className?: string;
  }) => {
    const isActive = sortColumn === column;
    const icon = isActive ? (
      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
    ) : (
      <ArrowUpDown className="h-3 w-3 opacity-50" />
    );

    return (
      <TableHead className={className}>
        <button
          onClick={() => handleSort(column)}
          className="flex items-center gap-1 hover:text-foreground transition-colors w-full justify-end"
        >
          {children}
          {icon}
        </button>
      </TableHead>
    );
  };

  const downloadCSV = () => {
    const headers = ['Пользователь', 'Подписчики', 'Взаимодействия', 'Просмотры', 'Reach', 'ER'];
    const rows = filteredAccounts.map(account => {
      const accountStats = stats[account.id];
      
      return [
        account.username || account.name || '',
        account.followersCount || 0,
        accountStats?.totalInteractions || 0,
        accountStats?.views || 0,
        accountStats?.reach || 0,
        accountStats?.er ? `${accountStats.er.toFixed(2)}%` : 'Нет данных'
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `accounts_stats_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };



  // Calculate totals (Instagram + TikTok)
  const totalFollowers =
    filteredAccounts.reduce((sum, acc) => sum + (acc.followersCount || 0), 0) +
    tiktokRows.reduce((sum, r) => sum + r.followers, 0);
  const totalInteractions =
    filteredAccounts.reduce((sum, acc) => sum + (stats[acc.id]?.totalInteractions || 0), 0) +
    tiktokRows.reduce((sum, r) => sum + r.interactions, 0);
  const totalViews =
    filteredAccounts.reduce((sum, acc) => sum + (stats[acc.id]?.views || 0), 0) +
    tiktokRows.reduce((sum, r) => sum + r.views, 0);
  const totalReach = filteredAccounts.reduce((sum, acc) => sum + (stats[acc.id]?.reach || 0), 0);
  
  // Calculate average ER по формуле: ER = (total_interactions / views) × 100
  const averageER = totalViews > 0 ? (totalInteractions / totalViews) * 100 : null;

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
        <div>
          <CardTitle>Статистика аккаунтов</CardTitle>
          <CardDescription>
            ER = (взаимодействия / просмотры) × 100
          </CardDescription>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DateRangeSelector value={dateRangeOption} onChange={setDateRangeOption} />
          <ExportMenu
            formats={[{ id: 'csv', label: 'CSV', description: 'Таблица аккаунтов за период — для Excel и Google Таблиц', icon: FileSpreadsheet }]}
            onSelect={downloadCSV}
            label="Выгрузить"
            variant="outline"
            size="sm"
            disabled={filteredAccounts.length === 0}
          />
          <Button variant="outline" size="sm" onClick={fetchAllStats} disabled={isAccountsLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${Object.values(loadingStats).some(Boolean) ? 'animate-spin' : ''}`} />
            Обновить
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loadingProgress && (
          <div className="mb-4 p-3 bg-muted rounded-md text-sm text-muted-foreground">
            {loadingProgress}
          </div>
        )}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[300px]">
                  <button
                    onClick={() => handleSort('name')}
                    className="flex items-center gap-1 hover:text-foreground transition-colors"
                  >
                    Аккаунт
                    {sortColumn === 'name' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 opacity-50" />
                    )}
                  </button>
                </TableHead>
                <SortableHeader column="followers" className="text-right">Подписчики</SortableHeader>
                <SortableHeader column="totalInteractions" className="text-right">Взаимодействия</SortableHeader>
                <SortableHeader column="views" className="text-right">Просмотры</SortableHeader>
                <SortableHeader column="reach" className="text-right">Reach</SortableHeader>
                <SortableHeader column="er" className="text-right">ER</SortableHeader>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isAccountsLoading ? (
                <TableSkeleton columns={6} rows={8} />
              ) : sortedAccounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                    {accounts.length > 0 ? 'Нет аккаунтов, соответствующих фильтру' : 'Нет подключенных аккаунтов'}
                  </TableCell>
                </TableRow>
              ) : (
                sortedAccounts.map((account) => {
                  const accountStats = stats[account.id];
                  const isLoading = loadingStats[account.id];
                  
                  return (
                    <TableRow key={account.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarImage src={account.profilePicture} alt={account.name} />
                            <AvatarFallback>{getInitials(account.name || account.username || '?')}</AvatarFallback>
                          </Avatar>
                          <div className="flex flex-col">
                            <span className="font-medium flex items-center gap-2">
                              {account.name}
                              <PlatformBadge platform="instagram" />
                            </span>
                            <span className="text-xs text-muted-foreground">@{account.username}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(account.followersCount)}
                      </TableCell>
                      <TableCell className="text-right">
                        {isLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        ) : (
                          formatNumber(accountStats?.totalInteractions)
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {isLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        ) : (
                          formatNumber(accountStats?.views)
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {isLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        ) : (
                          formatNumber(accountStats?.reach)
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {isLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        ) : accountStats?.er !== null && accountStats?.er !== undefined ? (
                          <span className="font-medium">{accountStats.er.toFixed(2)}%</span>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
              {tiktokRows.map((row) => (
                <TableRow key={`tt:${row.openId}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarFallback>{getInitials(row.username || row.displayName || 'TT')}</AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col">
                        <span className="font-medium flex items-center gap-2">
                          {row.username ? `@${row.username}` : row.displayName || row.openId}
                          <PlatformBadge platform="tiktok" />
                        </span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">{formatNumber(row.followers)}</TableCell>
                  <TableCell className="text-right">
                    {row.loading ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground inline" /> : formatNumber(row.interactions)}
                  </TableCell>
                  <TableCell className="text-right">
                    {row.loading ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground inline" /> : formatNumber(row.views)}
                  </TableCell>
                  {/* Reach недоступен в TikTok Display API */}
                  <TableCell className="text-right text-muted-foreground">—</TableCell>
                  <TableCell className="text-right">
                    {row.loading ? (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground inline" />
                    ) : row.er !== null ? (
                      <span className="font-medium">{row.er.toFixed(2)}%</span>
                    ) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell className="font-bold">Итого</TableCell>
                <TableCell className="text-right font-bold">{formatNumber(totalFollowers)}</TableCell>
                <TableCell className="text-right font-bold">{formatNumber(totalInteractions)}</TableCell>
                <TableCell className="text-right font-bold">{formatNumber(totalViews)}</TableCell>
                <TableCell className="text-right font-bold">{formatNumber(totalReach)}</TableCell>
                <TableCell className="text-right font-bold">
                  {averageER !== null ? `${averageER.toFixed(2)}%` : '—'}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}